import React, { useState } from "react";
import { api, formatDetail } from "@/lib/api";
import { toast } from "sonner";
import { RotateCcw, AlertTriangle, Download, Upload } from "lucide-react";

const RestoreBackup = ({ onDone, isOwner }) => {
  const [busy, setBusy] = useState(false);
  const [full, setFull] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (full && !window.confirm("Full restore will re-import results, incidents, assignments and sheet records from this backup. Continue?")) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("full", full ? "true" : "false");
      const { data } = await api.post("/system/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const r = data.restored;
      const extra = data.full ? `, ${r.results ?? 0} results, ${r.incidents ?? 0} incidents, ${r.assignments ?? 0} assignments` : "";
      toast.success(`Restored ${r.elections} elections, ${r.candidates} candidates, ${r.locations} locations${extra}.`);
      onDone?.();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail) || "Restore failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <label className={`border border-white/20 text-zinc-200 hover:bg-white hover:text-black text-xs uppercase tracking-[0.2em] font-semibold px-4 py-2 flex items-center gap-2 cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`} data-testid="restore-backup-btn">
        <Upload size={14}/>{busy ? "Restoring…" : full ? "Full Restore" : "Restore Backup"}
        <input type="file" accept=".zip" className="hidden" onChange={pick} data-testid="restore-backup-input"/>
      </label>
      {isOwner && (
        <label className="flex items-center gap-2 text-[11px] text-zinc-400 cursor-pointer select-none" title="Also restore results, incidents, assignments and sheet records (owner only)">
          <input type="checkbox" checked={full} onChange={(e) => setFull(e.target.checked)} className="accent-emerald-500 w-3.5 h-3.5" data-testid="restore-full-checkbox"/>
          Full (incl. results)
        </label>
      )}
    </div>
  );
};

export const ResetElectionPanel = ({ onDone, isOwner = true }) => {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [includeLocations, setIncludeLocations] = useState(false);
  const [busy, setBusy] = useState(false);
  const [backedUp, setBackedUp] = useState(false);

  const downloadBackup = () => {
    const token = localStorage.getItem("ec_token");
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/system/backup?auth=${token}`, "_blank");
    setBackedUp(true);
    toast.success("Backup download started — keep the ZIP somewhere safe.");
  };

  const run = async (e) => {
    e.preventDefault();
    if (!backedUp) downloadBackup();
    setBusy(true);
    try {
      if (!backedUp) await new Promise(r => setTimeout(r, 2500));
      const { data } = await api.post("/system/reset-election", { confirm, password, include_locations: includeLocations });
      const total = Object.values(data.deleted).reduce((a, b) => a + b, 0);
      toast.success(`System reset complete — ${total} records cleared. Ready for a fresh election.`);
      setOpen(false); setConfirm(""); setPassword(""); setBackedUp(false);
      onDone?.();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail) || "Reset failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-red-500/30 bg-red-950/10 mt-10 p-5" data-testid="reset-election-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-red-400 flex items-center gap-2"><AlertTriangle size={13}/>Danger Zone</div>
          <div className="font-semibold mt-2">Reset for fresh election</div>
          <div className="text-xs text-zinc-500 mt-1 max-w-xl">Wipes all results, sheets, incidents, assignments, elections, candidates, parties, audit log, lockouts and every non-admin user. Admin accounts and system settings are kept.{!isOwner && <span className="block mt-1 text-amber-400/80" data-testid="reset-owner-only-note">Only the system owner can run a reset.</span>}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={downloadBackup} className="border border-white/20 text-zinc-200 hover:bg-white hover:text-black text-xs uppercase tracking-[0.2em] font-semibold px-4 py-2 flex items-center gap-2" data-testid="download-backup-btn">
            <Download size={14}/>Download Backup
          </button>
          <RestoreBackup onDone={onDone} isOwner={isOwner}/>
          {isOwner && !open && (
            <button onClick={() => setOpen(true)} className="border border-red-500/50 text-red-300 hover:bg-red-500 hover:text-black text-xs uppercase tracking-[0.2em] font-semibold px-4 py-2 flex items-center gap-2" data-testid="reset-election-open-btn">
              <RotateCcw size={14}/>Reset System
            </button>
          )}
        </div>
      </div>
      {open && (
        <form onSubmit={run} className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3 items-end" data-testid="reset-election-form">
          <div className="md:col-span-3 text-xs text-zinc-400" data-testid="backup-status">
            {backedUp ? <span className="text-emerald-400">✓ Backup downloaded this session.</span> : "A full backup ZIP (all data + per-election CSVs) will be downloaded automatically before wiping."}
          </div>
          <label className="text-xs text-zinc-400">Type <span className="font-mono text-red-300">RESET</span>
            <input value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 w-full bg-transparent border-b border-white/20 py-2 outline-none focus:border-red-400 text-sm font-mono" data-testid="reset-confirm-input"/>
          </label>
          <label className="text-xs text-zinc-400">Your password
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full bg-transparent border-b border-white/20 py-2 outline-none focus:border-red-400 text-sm" data-testid="reset-password-input"/>
          </label>
          <div className="flex items-center gap-3">
            <button type="submit" disabled={busy || confirm !== "RESET" || !password} className="bg-red-500 text-black text-xs uppercase tracking-[0.2em] font-semibold px-4 py-2 disabled:opacity-40" data-testid="reset-election-submit-btn">{busy ? "Resetting…" : "Wipe & Reset"}</button>
            <button type="button" onClick={() => setOpen(false)} className="text-xs text-zinc-400 hover:text-white" data-testid="reset-election-cancel-btn">Cancel</button>
          </div>
          <label className="md:col-span-3 flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
            <input type="checkbox" checked={includeLocations} onChange={(e) => setIncludeLocations(e.target.checked)} className="accent-red-500 w-4 h-4" data-testid="reset-include-locations-checkbox"/>
            Also delete the location hierarchy (regions, constituencies, polling units)
          </label>
        </form>
      )}
    </div>
  );
};
